import { useState, useCallback, useEffect, memo } from 'react';
import  {
    Backdrop,    
    Button,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    Divider,
    InputAdornment,    
    InputLabel,
    IconButton,
    FormControl,
    FormLabel,
    OutlinedInput,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography,         
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { encodeBase64UUID } from '../../security/encdec64';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Transition, CalculateRoundedTip } from './utils';

const Percentages = {
    Tip15Pct: '15%',
    Tip18Pct: '18%',
    Tip22Pct: '22%',
}

export default function TipDialog({
    company,
    seat,
    order,
    payment,
    setPayment,
    isOpen,
    openDialog,
    closeDialog
}) {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [value, setValue] = useState('');
    const [tip, setTip] = useState('0.00');
    const [total, setTotal] = useState(0.00);
    const tax = payment.stateTax + payment.cityTax;

    const handleChange = useCallback(({target: {name, value}}) => {
        if (name === 'tip') {
            setValue(value);
            switch(value) {
                case Percentages.Tip15Pct:
                    setTip(CalculateRoundedTip(payment.subtotal, 0.15).toFixed(2));
                    break;
                case Percentages.Tip18Pct:
                    setTip(CalculateRoundedTip(payment.subtotal, 0.18).toFixed(2));
                    break;
                case Percentages.Tip22Pct:
                    setTip(CalculateRoundedTip(payment.subtotal, 0.22).toFixed(2));
                    break;
            }
        }
        else if (name === 'otherTip') {
            if (/^\d*\.?\d*$/.test(value)) {
                setTip(value);
            }
        }
    }, [payment.subtotal]);

    const handleBlur = useCallback(() => {
        setTip(Number(tip).toFixed(2));
    }, [tip]);

    const handleFocus = useCallback(() => {
        setValue('');
    }, []);

    const handleContinue = useCallback(async (e) => {
        e.preventDefault();

        setIsLoading(true);
        try {
            const orderParams = new URLSearchParams({
                restaurant: encodeBase64UUID(company._id),
                seat: encodeBase64UUID(seat._id)
            })
            const orderReq = await fetch(`${process.env.REACT_APP_API_URL}/order?${orderParams}`,{
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });

            if (!orderReq.ok) {
                const e = await orderReq.json();
                throw new Error (e.error);
            }

            const orderData = await orderReq.json();

            const orderItems = order.map(({ name, terminal, price, quantity, total }) => ({
                name,
                terminal,
                price,
                quantity,
                total
            }));

            const paymentReq = await fetch(`${process.env.REACT_APP_API_URL}/stripe/payment-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${orderData.token}`
                },
                body: JSON.stringify(
                    {
                        company: company._id,
                        seat: seat._id,
                        items: orderItems,
                        tip: payment.tip,
                        cityTax: payment.cityTax,
                        stateTax: payment.stateTax,
                        subtotal: payment.subtotal,
                        total: payment.total,
                    }
                )
            });

            if (!paymentReq.ok) {
                const e = await paymentReq.json();
                throw new Error (e.error);
            }

            const paymentData = await paymentReq.json()
            setPayment({orderToken: orderData.token,
                        customer: paymentData.customer,
                        clientSecret: paymentData.clientSecret,
                        subtotal: paymentData.subtotal,
                        tip: paymentData.tip,
                        cityTax: paymentData.cityTax,
                        stateTax: paymentData.stateTax,
                        total: paymentData.total
            });
            closeDialog('tipSelection');
            openDialog('paymentMethod');
        }
        catch(error) {
            console.error(error);
        }
        finally {
            setIsLoading(false);
        }
    }, [
        company._id,
        seat._id,
        order,
        payment,
        setPayment,
        closeDialog,
        openDialog
    ]);

    useEffect(() => {
        const tipAmount = Number(tip);
        const newTotal = payment.subtotal + tax + tipAmount;
        payment.total = newTotal;
        payment.tip = tipAmount;
        setTotal(payment.total);
    }, [payment, tax, tip]);

    return (
        <Dialog
            open={isOpen('tipSelection')}
            onClose={() => closeDialog('tipSelection')}
            keepMounted
            fullScreen
            slots={{transition: Transition}}
            slotProps={{
                paper: {
                    sx: {
                        position: 'relative',
                        maxHeight: '100vh',
                    }
                },
            }}>         
        {isLoading && (
            <Backdrop
                sx={(theme) => ({ color: '#FFF', zIndex: theme.zIndex.drawer + 1 })}
                open={isLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        )}
        <DialogTitle>
            <Box display="flex" alignItems="center">
                <IconButton onClick={() => closeDialog('tipSelection')} edge="start" sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">{t('tipTitle')}</Typography>
            </Box>
        </DialogTitle>
        <DialogContent>
            <FormControl fullWidth variant="standard">
                <FormLabel sx={{ mb: 2, pl: 0.5 }}>{t('tipDisclaimer')}</FormLabel>
                <RadioGroup name="tip" value={value}>
                    {Object.values(Percentages).map((tip, i) => (
                        <TipRadio
                            key={i}
                            name='tip'
                            value={tip}
                            selected={value === tip}
                            onSelect={handleChange}
                        />
                    ))}
                </RadioGroup>
            </FormControl>
            <FormControl fullWidth sx={{mt: 2}}>
                <InputLabel>{t('otherTip')}</InputLabel>
                <OutlinedInput sx={{borderRadius: 2}}
                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    label={t('otherTip')}
                    name='otherTip'
                    value={tip}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                />
            </FormControl>
            <Stack spacing={1} mt={2}>
                <Box>
                    <Box display="flex" justifyContent="space-between">
                        <Typography fontWeight="bold">Subtotal</Typography>
                        <Typography fontWeight="bold">${payment.subtotal.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" ml={1}>
                        <Typography>{t('taxLabel')}</Typography>
                        <Typography>${tax.toFixed(2)}</Typography>
                    </Box> 
                    <Box display="flex" justifyContent="space-between" ml={1}>
                        <Typography >{t('tipLabel')}</Typography>
                        <Typography >${Number(tip).toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between">
                        <Typography fontWeight="bold">Total</Typography>
                        <Typography fontWeight="bold">${total.toFixed(2)}</Typography>
                    </Box>                    
                </Box>
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button
                disabled={isLoading}
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AttachMoneyIcon />}
                onClick={handleContinue}>
                {t('tipContinueButton')}
            </Button>
        </DialogActions>
      </Dialog>
    );
}

const TipRadio = memo(({ name, value, selected, onSelect }) => (
  <Paper
    onClick={() => onSelect({ target: { name, value } })}
    elevation={0}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 1,
      mb: 1,
      borderRadius: 2,
      border: '1px solid #ccc',
      cursor: 'pointer',
      backgroundColor: selected ? '#f0f0f0' : 'transparent',
    }}
  >
    <Typography sx={{ ml: 2 }}>{value}</Typography>
    <Radio
      value={value}
      checked={selected}
      onChange={onSelect}
      sx={{
        color: 'gray',
        '&.Mui-checked': {
          color: '#4B7359',
        },
      }}
    />
  </Paper>
));
